import { FunctionComponent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import Spinner from 'components/UI/Spinner';
import BoardingIsEmpty from '../List/BoardingIsEmpty';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { BoardingForm } from '../Form/BoardingForm';
import BoardingServicesTable from '../List/BoardingServicesTable';
import { Service, isBoardingService } from 'model/Service';
import { useAuth, useBoardingServices, DEFAULT_PAGE_SIZE, DEFAULT_PAGE, useApiClientWrapper } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import styles from './BoardingServices.module.scss';
import { resetServicesThunk } from 'actions/services/ServicesActions';
import { Room } from '@spike/room-model';
import { searchThunk } from '@spike/rooms-action';
import Button from 'components/UI/V3/Button/Button';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';

const BoardingServices: FunctionComponent = () => {
    const {
        services: boardingServices,
        savedService,
        meta,
        fetchBoardingServices,
        duplicateBoardingService,
        deleteBoardingService,
        toggleBoardingServiceStatus
    } = useBoardingServices();

    const dispatch = useDispatch();
    const auth = useAuth();
    const apiClientWrapper = useApiClientWrapper();

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    // Get rooms from Redux state
    const roomsFromState = useSelector<RootState, any>(state => state.rooms?.rooms);

    const [loading, setLoading] = useState({ services: false, initialLoading: true, rooms: false });
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [marketplaceIds, setMarketplaceIds] = useState<Array<number>>(() =>
        auth.marketplaceId ? [auth.marketplaceId] : []
    );
    const [pageInfo, setPageInfo] = useState({
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [showAddBoarding, setShowAddBoarding] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [lastSavedServiceId, setLastSavedServiceId] = useState<number | undefined>();
    const [rooms, setRooms] = useState<Room[]>([]);
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const fetchServices = () => {
        setLoading(prev => ({ ...prev, services: true }));
        fetchBoardingServices(pageInfo.page, pageInfo.pageSize, selectedOption, searchText, marketplaceIds);
    };

    const fetchRooms = async () => {
        setLoading(prev => ({ ...prev, rooms: true }));
        try {
            const roomsInfo = { searchText: '', page: 1, pageSize: 30 };
            await dispatch(searchThunk(apiClientWrapper, roomsInfo.searchText, roomsInfo.page, roomsInfo.pageSize));
        } catch (error) {
            // Silent error handling
        } finally {
            setLoading(prev => ({ ...prev, rooms: false }));
        }
    };

    useEffect(() => {
        dispatch(resetServicesThunk());
        fetchServices();
        fetchRooms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Only fetch if not in initial loading state
        if (!loading.initialLoading) {
            fetchServices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageInfo, selectedOption, searchText, marketplaceIds]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.FetchSuccess:
            case ServicesStatus.Error:
                setLoading(prev => ({ ...prev, services: false, initialLoading: false }));
                break;
            case ServicesStatus.SaveBulkSuccess:
                fetchServices();
                break;
        }
    }, [servicesStatus]);

    // Update rooms when they're fetched
    useEffect(() => {
        if (roomsFromState?.page) {
            // Filter for boarding room type (id: 2)
            const boardingRooms = roomsFromState.page.filter((room: Room) => room.roomType?.id === 2);
            setRooms(boardingRooms);
            setLoading(prev => ({ ...prev, rooms: false }));
        }
    }, [roomsFromState]);

    // POST-SAVE REFRESH (legacy single-service save)
    useEffect(() => {
        if (savedService && isBoardingService(savedService)) {
            // Only fetch fresh data if this is a new save
            if (savedService.id && savedService.id !== lastSavedServiceId) {
                setLastSavedServiceId(savedService.id);
                fetchServices();
            }
        }
    }, [savedService]);

    // HANDLERS with immediate loading control (prevents visual flashes)
    const handlePageChange = (page: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo(prev => ({ ...prev, page }));
    };

    const handlePageSizeChange = (size: number) => {
        setLoading(prev => ({ ...prev, services: true }));
        setPageInfo({ page: 1, pageSize: size });
    };

    const handleStatusChange = (option: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSelectedOption(option);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (text: string) => {
        setLoading(prev => ({ ...prev, services: true }));
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleMarketplaceIdsChange = (ids: Array<number>) => {
        setLoading(prev => ({ ...prev, services: true }));
        setMarketplaceIds(ids);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    };

    const handleEditService = (service: Service) => {
        setEditingService(service);
        setShowAddBoarding(true);
    };

    const handleDuplicateService = (service: Service) => {
        duplicateBoardingService(service);
    };

    const handleDeactivateService = (service: Service) => {
        toggleBoardingServiceStatus(service, {
            page: pageInfo.page,
            per: pageInfo.pageSize,
            status: selectedOption,
            searchText: searchText,
            marketplaceIds
        });
    };

    const handleCloseModal = () => {
        setShowAddBoarding(false);
        setEditingService(null);
    };

    const headerRightContent = (showManageRooms: boolean) => (
        <div className={styles.headerRightContent}>
            {showManageRooms && (
                <a
                    href="/business_settings/info/rooms"
                    className={styles.manageRooms}
                >
                    Manage Rooms
                </a>
            )}
            <OrganizationMarketplacesFilter
                selected={marketplaceIds}
                singleSelection
                onChangeSelection={handleMarketplaceIdsChange}
                className={styles.locationsFilter}
            />
            <Button
                leftIcon={faPlus}
                label={isMobile ? 'Add' : 'Add Service'}
                onClick={() => setShowAddBoarding(true)}
                id="boarding_add_button"
            />
        </div>
    );

    const renderContent = () => {
        // Initial loading
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        // Business has no boarding services (with default filters)
        if (
            boardingServices.length === 0 &&
            meta?.totalServices === 0 &&
            selectedOption === 'active' &&
            !searchText &&
            !loading.services
        ) {
            return (
                <div className={styles.root}>
                    <BoardingIsEmpty
                        onClick={() => setShowAddBoarding(true)}
                        headerRightContent={headerRightContent(false)}
                    />
                </div>
            );
        }

        // Business has boarding services → show full layout
        return (
            <div className={styles.root}>
                {/* Header always visible when there's service context */}
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={
                            <SelectionBar
                                options={[
                                    { id: 'active', name: 'Active' },
                                    { id: 'inactive', name: 'Inactive' },
                                    { id: 'all', name: 'All' }
                                ]}
                                selectedId={selectedOption}
                                onSelect={handleStatusChange}
                                className={styles.selectionBar}
                            />
                        }
                        displayButton={true}
                        onClick={() => setShowAddBoarding(true)}
                        id="boarding_add_button"
                        className={styles.headerContainer}
                        customButton={headerRightContent(true)}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by service name"
                        className={styles.searchBarContainer}
                    />
                </div>

                {/* Table handles: loading, error, filtered empty, and data */}
                <div className={styles.tableContainer}>
                    <BoardingServicesTable
                        services={boardingServices}
                        meta={meta}
                        rooms={rooms}
                        isLoading={loading.services || loading.rooms}
                        isEmpty={boardingServices.length === 0 && !loading.services}
                        onEdit={handleEditService}
                        onDuplicate={handleDuplicateService}
                        onDeactivate={handleDeactivateService}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onRetry={() => fetchServices()}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showAddBoarding && (
                <BoardingForm
                    mode={editingService ? 'edit' : 'add'}
                    service={editingService || undefined}
                    rooms={rooms}
                    roomsLoading={loading.rooms}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default BoardingServices;
